import { Close, PermPhoneMsg } from "@mui/icons-material";
import {
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { DisplayQuotaUsage } from "../../App/Components/QuotaMessaging/QuotaMessaging";
import { useCandidateStopCompliance, useGetMessageCalendarConfigs } from "../../App/Queries/MessageQueries";
import {
  useMessageDialogHandlers,
  useMessageDialogState,
} from "../../App/State/Hooks/MessageDialog/useMessageDialog";
import { useMessageInputHandlers } from "../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageScheduleHandlers,
  useMessageScheduleState,
} from "../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";
import { ICandidate } from "../../App/Typings/Candidate";
import { ICandidateProfile } from "../../App/Typings/CandidateProfile";
import { ICandidateWithMessageInfo, IMessageSendPost } from "../../App/Typings/MessageText";
import { useGroupCandidateSelectionContext } from "../../GroupProfile/State/GroupCandidateSelectionContext";
import { useManageCandidateSelectionContext } from "../../ManageCandidates/State/ManageCandidateContext";
import { toNearestFiveMinutes } from "../../MessagingCenter/Utils";
import { IMessageContainers } from "../Enums/MessageEnums";
import { BuildPhoneNumber, getMessageContainerDisplayName } from "../FunctionMethods/MessageFunctions";
import { RegulationText } from "../SendGroupMessage/Components/SharedComponents/RegulationText";
import { NoPhoneNumbersAlert, NoValidPhoneNumbersAlert } from "./Components/Alerts";
import MessageHistory from "./Components/MessageHistory";
import SendNowFooter from "./Components/SendNowFooter";
import { TextMessageSchedule } from "./Components/TextMessageSchedule";
import useMessageMutationState from "./State/useMessageMutationState";

export default function MessageDialogCandidateText({
  candidate,
}: {
  candidate?: ICandidate | ICandidateProfile | ICandidateWithMessageInfo;
}) {
  const {
    getters: { actionCandidate: candidateFromManageContext },
  } = useManageCandidateSelectionContext();
  const {
    getters: { actionCandidate: candidateFromGroupContext },
  } = useGroupCandidateSelectionContext();

  candidate = useMemo(
    () =>
      candidateFromGroupContext
        ? candidateFromGroupContext
        : candidateFromManageContext
          ? candidateFromManageContext
          : candidate,
    [candidateFromGroupContext, candidateFromManageContext],
  );

  const { profile } = useUserProfileStore();
  const [replyMessageId, setReplyMessageId] = useState<string>("");
  const [disableNotificationToggle, setDisableNotificationToggle] = useState<boolean>(false);
  const [notifyMe, setNotifyMe] = useState<boolean>(profile.notifyOnTextReply ?? false);
  const { isCandidateTextOpen, containerType, dialogTitle } = useMessageDialogState();
  const { handleChangeContainerType, handleCloseDialog } = useMessageDialogHandlers();
  const handleCloseDialogAndReset = () => {
    handleCloseDialog();
    setNotifyMe(profile.notifyOnTextReply ?? false);
  };

  const { handleCloseSendMenu, handleValidateMessageInput } = useMessageInputHandlers();

  const { messageType, msgListQueue, scheduleDateTime } = useMessageScheduleState();
  const { handleClickScheduleButton, handleCalendarChange } = useMessageScheduleHandlers();

  const { data: scheduleConfig, isLoading: scheduleConfigLoading } = useGetMessageCalendarConfigs(
    candidate?.clientId ?? "",
    candidate?.id ?? "",
    messageType,
  );
  const now = dayjs().utc();
  const isValidTime =
    now.isAfter(dayjs(scheduleConfig?.startDate)) && now.isBefore(dayjs(scheduleConfig?.endDate));

  const hasPhoneNumbers = Boolean(candidate?.phoneNumbers?.find((phoneNumber) => phoneNumber.isValid));

  const candidateId = candidate?.id || "";

  const { data: isStop } = useCandidateStopCompliance(candidateId);

  const messageDialogTitle = dialogTitle || getMessageContainerDisplayName(containerType);
  const messageDialogHeader = candidate
    ? candidate.firstName + " " + candidate.lastName
    : "Unknown Candidate";
  const messagePhoneNumberDisplay = () => {
    if (!hasPhoneNumbers) {
      return "No Phone Number";
    }
    return BuildPhoneNumber(candidate);
  };

  const sendMessageBody: IMessageSendPost = useMemo(
    () => ({
      candidates: [candidateId],
      isEmail: false,
      isText: true,
      isGroup: false,
      messages: msgListQueue,
      userId: "overrides in messagequeries",
      uiComponent: "overrides in messagequeries",
      userName: "",
      replyMessageId,
      notify: notifyMe,
      notifyEmail: "overrides in messagequeries",
    }),
    [candidateId, msgListQueue, replyMessageId, notifyMe],
  );

  const messageMutationState = useMessageMutationState(sendMessageBody);
  const {
    handleSendMessages,
    postSendMessageIsLoading,
    upsertMessageTemplateLoading,
    tempMessages,
    setTempMessages,
  } = messageMutationState;

  const canReceiveText = hasPhoneNumbers && !isStop;
  const isScheduledMessage = containerType === IMessageContainers.CandidateScheduledText;
  const isValidMessage = handleValidateMessageInput();

  const mutationLoading = postSendMessageIsLoading || upsertMessageTemplateLoading || scheduleConfigLoading;
  const disableMessageInput = !canReceiveText || mutationLoading || !scheduleConfig?.isValidNumber;
  const disableSendMenuButton = disableMessageInput || !isValidMessage;
  const disableSendMessageAction = disableSendMenuButton;

  const handleSelectScheduleText = () => {
    const newTime = scheduleDateTime ? dayjs(scheduleDateTime) : toNearestFiveMinutes();
    handleCalendarChange(newTime);
    handleChangeContainerType(IMessageContainers.CandidateScheduledText);
  };
  const handleSelectTextNow = () => {
    const newTime = scheduleDateTime ? dayjs(scheduleDateTime) : toNearestFiveMinutes();
    handleCalendarChange(newTime);
    handleChangeContainerType(IMessageContainers.CandidateTextNow);
    handleCloseSendMenu();
    handleClickScheduleButton();
  };

  const dialogContentStyle = {
    display: "flex",
    height: "100%",
    flexDirection: "column",
  };

  let DialogContentDisplay = (
    <MessageHistory
      temporaryMessages={tempMessages}
      isStop={isStop || false}
      candidateId={candidateId}
      candidate={candidate}
      onTempMessageReloadLoad={() => setTempMessages([])}
      setReplyMessageId={setReplyMessageId}
      setDisableNotification={setDisableNotificationToggle}
    />
  );

  if (!hasPhoneNumbers) {
    DialogContentDisplay = (
      <>
        {candidate?.phoneNumbers && candidate?.phoneNumbers.length > 0 ? (
          <NoValidPhoneNumbersAlert />
        ) : (
          <NoPhoneNumbersAlert />
        )}
        {DialogContentDisplay}
      </>
    );
  } else if (isScheduledMessage) {
    DialogContentDisplay = (
      <TextMessageSchedule
        selectedCandidatesCount={1}
        messageMutationState={messageMutationState}
        notifyMe={notifyMe}
        setNotifyMe={setNotifyMe}
      />
    );
  } else if (!scheduleConfig?.isValidNumber) {
    DialogContentDisplay = <NoValidPhoneNumbersAlert message={scheduleConfig?.errorMessage} />;
  } else {
    dialogContentStyle["flexDirection"] = "column-reverse";
  }

  useEffect(() => {
    if (isCandidateTextOpen) {
      setNotifyMe(profile.notifyOnTextReply ?? false);
    }
  }, [isCandidateTextOpen, profile.notifyOnTextReply]);

  useEffect(() => {
    return () => {
      handleCloseDialogAndReset();
    };
  }, []);

  return (
    <Dialog
      id="message-dialog"
      data-test="message-text-dialog-window"
      aria-labelledby="message-dialog-title"
      open={isCandidateTextOpen}
      onClose={handleCloseDialogAndReset}
      scroll="paper"
      fullWidth={true}
      maxWidth="lg"
      TransitionProps={{ unmountOnExit: true }}
      PaperProps={{
        sx: {
          maxHeight: { xs: "calc(100vh - 1rem)", sm: "calc(100vh - 64px)" },
          height: { xs: "100vh", sm: "90vh" },
          width: { xs: "calc(100% - 1rem)", sm: "calc(100% - 2rem)" },
          m: { xs: "1rem", sm: "2rem" },
        },
      }}
    >
      <DialogTitle
        id="directmessage-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          px: { xs: 2, md: 4 },
          color: "white",
          backgroundColor: "#00af4a",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
          <Box>{messageDialogTitle + " : " + messageDialogHeader}</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "1ch" }}>
            <PermPhoneMsg />
            {messagePhoneNumberDisplay()}
          </Box>
        </Box>
        <Box sx={{ ml: { xs: "auto", md: 0 } }}>
          <IconButton
            color="inherit"
            onClick={handleCloseDialogAndReset}
            aria-label="Close Delivered Messages Window"
            aria-controls="delivered-message-window"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers={true} sx={dialogContentStyle}>
        {DialogContentDisplay}
      </DialogContent>

      <DialogActions sx={{ py: 2, px: 3, flexDirection: "column" }}>
        <Collapse in={!isScheduledMessage} sx={{ width: 1 }}>
          <SendNowFooter
            disableInput={disableMessageInput}
            disableSendButton={disableSendMessageAction}
            disableNotificationToggle={disableNotificationToggle}
            handleSendMessages={handleSendMessages}
            isValidTime={isValidTime}
            notifyMe={notifyMe}
            setNotifyMe={setNotifyMe}
          />
        </Collapse>
        <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Tooltip title="Schedule messages to be sent at a later time" arrow>
            <FormControlLabel
              control={
                <Switch
                  data-test="schedule-messages-switch"
                  checked={isScheduledMessage}
                  onChange={() => (isScheduledMessage ? handleSelectTextNow() : handleSelectScheduleText())}
                  inputProps={{ "aria-label": "controlled" }}
                  color="secondary"
                />
              }
              label="Schedule Messages"
              sx={{ flexGrow: { xs: 1, sm: 0 } }}
            />
          </Tooltip>
          <Box sx={{ flexGrow: 1 }}>
            <RegulationText sx={{ display: "block", ml: "auto" }} />
            <DisplayQuotaUsage
              channel="text"
              recipientCount={1}
              isScheduled={isScheduledMessage}
              sx={{ display: "block", ml: "auto" }}
            />
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
